import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AnalyticsManager from '../utils/AnalyticsManager';
import styles from './LanguageSwitcher.module.css';

interface LanguageSwitcherProps {
  className?: string;
  showFlags?: boolean;
  showText?: boolean;
  variant?: 'dropdown' | 'toggle' | 'buttons' | 'floating';
  isHidden?: boolean;
  isSidebarOpen?: boolean;
}

const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({
  className = '',
  showFlags = true,
  showText = true,
  variant = 'toggle',
  isHidden = false,
  isSidebarOpen = false
}) => {
  const { t, i18n } = useTranslation();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const currentLanguage = i18n.language;

  const handleLanguageChange = async (language: string) => {
    if (language === currentLanguage) return;

    const analytics = AnalyticsManager.getInstance();
    analytics.trackLanguageChange(currentLanguage, language);

    setIsLoading(true);
    
    try {
      await i18n.changeLanguage(language);
      setIsDropdownOpen(false);
    } catch (error) {
      console.error('Failed to change language:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const languageConfigs = {
    en: { code: 'en', flag: '🇺🇸', name: 'English', nativeName: 'English' },
    nl: { code: 'nl', flag: '🇧🇪', name: 'Dutch', nativeName: 'Nederlands' },
    fr: { code: 'fr', flag: '🇧🇪', name: 'French', nativeName: 'Français' }
  };

  const availableLanguages = Object.values(languageConfigs);
  const currentConfig = languageConfigs[currentLanguage as keyof typeof languageConfigs] || languageConfigs.en;

  if (variant === 'floating') {
    // Cycle through languages: en -> nl -> fr -> en
    const getNextLanguage = () => {
      switch (currentLanguage) {
        case 'en': return 'nl';
        case 'nl': return 'fr';
        case 'fr': return 'en';
        default: return 'en';
      }
    };
    
    const nextLanguage = getNextLanguage();
    const nextConfig = languageConfigs[nextLanguage as keyof typeof languageConfigs];

    const buttonClasses = [
      styles["language-switcher"],
      isSidebarOpen ? styles["sidebar-open"] : "",
      isHidden ? styles.hidden : ""
    ].filter(Boolean).join(" ");

    return (
      <button
        onClick={() => handleLanguageChange(nextLanguage)}
        disabled={isLoading}
        className={buttonClasses}
        title={t('navigation.languageSwitch')}
        aria-label={`Switch to ${nextConfig.nativeName}`}
      >
        {showFlags && (
          <span className={styles["language-flag"]} role="img" aria-label={currentConfig.name}>
            {currentConfig.flag}
          </span>
        )}
        {showText && (
          <span className={styles["language-text"]}>
            {currentConfig.code.toUpperCase()}
          </span>
        )}
        {isLoading && (
          <div className={styles["loading-spinner"]} />
        )}
      </button>
    );
  }

  if (variant === 'toggle') {
    // Cycle through languages: en -> nl -> fr -> en
    const getNextLanguage = () => {
      switch (currentLanguage) {
        case 'en': return 'nl';
        case 'nl': return 'fr';
        case 'fr': return 'en';
        default: return 'en';
      }
    };
    
    const nextLanguage = getNextLanguage();
    const nextConfig = languageConfigs[nextLanguage as keyof typeof languageConfigs];

    return (
      <button
        onClick={() => handleLanguageChange(nextLanguage)}
        disabled={isLoading}
        className={`
          inline-flex items-center gap-2 px-3 py-2 text-sm font-medium
          bg-white border border-gray-300 rounded-md shadow-sm
          hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
          disabled:opacity-50 disabled:cursor-not-allowed
          ${className}
        `}
        title={t('navigation.languageSwitch')}
      >
        {showFlags && (
          <span className="text-lg" role="img" aria-label={nextConfig.name}>
            {nextConfig.flag}
          </span>
        )}
        {showText && (
          <span>
            {nextConfig.code.toUpperCase()}
          </span>
        )}
        {isLoading && (
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        )}
      </button>
    );
  }

  if (variant === 'buttons') {
    return (
      <div className={`inline-flex rounded-md shadow-sm ${className}`} role="group">
        {availableLanguages.map((config) => (
          <button
            key={config.code}
            onClick={() => handleLanguageChange(config.code)}
            disabled={isLoading}
            className={`
              px-3 py-2 text-sm font-medium border
              ${config.code === currentLanguage
                ? 'bg-blue-600 text-white border-blue-600 z-10'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }
              ${config.code === 'en' ? 'rounded-l-md' : ''}
              ${config.code === 'nl' ? '-ml-px' : ''}
              ${config.code === 'fr' ? 'rounded-r-md -ml-px' : ''}
              focus:outline-none focus:ring-2 focus:ring-blue-500
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            <div className="flex items-center gap-2">
              {showFlags && (
                <span className="text-lg" role="img" aria-label={config.name}>
                  {config.flag}
                </span>
              )}
              {showText && <span>{config.code.toUpperCase()}</span>}
            </div>
          </button>
        ))}
        {isLoading && (
          <div className="flex items-center px-2">
            <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
          </div>
        )}
      </div>
    );
  }

  // Dropdown variant
  return (
    <div className={`relative inline-block text-left ${className}`}>
      <button
        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
        disabled={isLoading}
        className="
          inline-flex items-center gap-2 px-3 py-2 text-sm font-medium
          bg-white border border-gray-300 rounded-md shadow-sm
          hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500
          disabled:opacity-50 disabled:cursor-not-allowed
        "
      >
        {showFlags && (
          <span className="text-lg" role="img" aria-label={currentConfig.name}>
            {currentConfig.flag}
          </span>
        )}
        {showText && <span>{currentConfig.nativeName}</span>}
        <svg
          className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
        {isLoading && (
          <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
        )}
      </button>

      {isDropdownOpen && (
        <div className="
          absolute right-0 z-10 mt-2 w-48 origin-top-right
          bg-white border border-gray-300 rounded-md shadow-lg
          ring-1 ring-black ring-opacity-5 focus:outline-none
        ">
          <div className="py-1">
            {availableLanguages.map((config) => (
              <button
                key={config.code}
                onClick={() => handleLanguageChange(config.code)}
                disabled={isLoading}
                className={`
                  w-full text-left px-4 py-2 text-sm flex items-center gap-3
                  ${config.code === currentLanguage
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-700 hover:bg-gray-50'
                  }
                  disabled:opacity-50 disabled:cursor-not-allowed
                `}
              >
                {showFlags && (
                  <span className="text-lg" role="img" aria-label={config.name}>
                    {config.flag}
                  </span>
                )}
                <div className="flex flex-col">
                  <span className="font-medium">{config.nativeName}</span>
                  {showText && (
                    <span className="text-xs text-gray-500">{config.name}</span>
                  )}
                </div>
                {config.code === currentLanguage && (
                  <svg className="w-4 h-4 ml-auto text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Overlay to close dropdown when clicking outside */}
      {isDropdownOpen && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setIsDropdownOpen(false)}
        />
      )}
    </div>
  );
};

export default LanguageSwitcher;